import axiosInstance from '../api';

interface ChatbotMessage {
  message: string;
  conversationId?: string;
  context?: string;
  userId?: string;
  /** Where in the app the question was asked from. */
  pageContext?: { route?: string; label?: string; listingId?: string };
}

interface PropertyResult {
  mlsNumber: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  price: number;
  beds: number;
  baths: number;
  sqft: number;
  propertyType: string;
  photos: string[];
  description?: string;
  daysOnMarket?: number;
  listingAgent?: string;
  subdivision?: string;
}

export type RevaObjectType =
  | 'listing'
  | 'offer'
  | 'transaction'
  | 'tour'
  | 'agent';

/** The thing an action acts on, or an object Reva cited — rendered as a card, never as prose. */
export interface RevaActionObject {
  type: RevaObjectType;
  id: string | null;
  title: string;
  subtitle: string | null;
  priceLabel: string | null;
  photo: string | null;
  facts: string[];
  href: string | null;
  status: string | null;
}

export interface RevaActionParam {
  key: string;
  label: string;
  value: string;
}

/** A side effect Reva is proposing. Nothing happens until the buyer confirms it. */
export interface RevaAction {
  pendingActionId: string;
  kind: string;
  title: string;
  prompt: string;
  /** Seeds the composer when the buyer taps "Change". */
  editPrompt: string;
  confirmLabel: string;
  object: RevaActionObject | null;
  params: RevaActionParam[];
  expiresAt: string;
}

export interface RevaActionResult {
  pendingActionId: string;
  conversationId: string;
  status: 'confirmed' | 'cancelled' | 'failed';
  message: string;
}

/** Sent instead of an answer when Reva will not take the question on and a person should. */
export interface RevaHandoff {
  kind: 'text_caroline';
  label: string;
  smsBody: string;
}

interface ChatbotResponse {
  response: string;
  conversationId: string;
  context?: string;
  properties?: PropertyResult[];
  action?: RevaAction;
  objects?: RevaActionObject[];
  handoff?: RevaHandoff;
}

interface Conversation {
  id: string;
  userId?: string;
  context: string;
  messages: Message[];
  createdAt: Date;
  updatedAt: Date;
}

interface Message {
  id: string;
  conversationId: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  createdAt: Date;
}

const chatbotService = {
  // Send a message to the chatbot
  sendMessage: async (data: ChatbotMessage): Promise<ChatbotResponse> => {
    const res = await axiosInstance.post<ChatbotResponse>('/chatbot', data);
    return res.data;
  },

  /**
   * Confirm or cancel the action Reva parked on this conversation. The server runs exactly the
   * parked action, once, and only for the signed-in owner of the conversation.
   */
  resolveAction: async (
    pendingActionId: string,
    data: { conversationId: string; decision: 'confirm' | 'cancel' }
  ): Promise<RevaActionResult> => {
    const res = await axiosInstance.patch<RevaActionResult>(
      `/chatbot/actions/${encodeURIComponent(pendingActionId)}`,
      data
    );
    return res.data;
  },

  // Get conversation history
  getConversation: async (conversationId: string): Promise<Conversation> => {
    const res = await axiosInstance.get<Conversation>(
      `/chatbot/${conversationId}`
    );
    return res.data;
  },

  // Get all conversations for the current user
  getConversations: async (): Promise<Conversation[]> => {
    const res = await axiosInstance.get<Conversation[]>('/chatbot');
    return res.data;
  },

  // Delete a conversation
  deleteConversation: async (conversationId: string): Promise<void> => {
    await axiosInstance.delete(`/chatbot/${conversationId}`);
  },
};

export default chatbotService;
